import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import ErrorBanner from '../components/ErrorBanner';
import HealthChecksList from '../components/HealthChecksList';
import type {
  AppDefaultsResponse,
  AppItem,
  DnsDomainsResponse,
  QuickstartAppResponse,
  SanityChecksResponse,
  Server,
  TemplateOption,
  TemplateResponse
} from '../types';
import { reportError, reportErrorLocal } from '../utils/errors';
import AppsPageList from './AppsPageList';

type AppsPageProps = {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
  isAdmin: boolean;
  /** Legacy ViewSite users retain the historic app-management experience. */
  canManageApps: boolean;
};

export default function AppsPage({
  apiClient,
  setError,
  isAdmin,
  canManageApps
}: AppsPageProps) {
  const navigate = useNavigate();
  const [apps, setApps] = useState<AppItem[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsLoadFailed, setAppsLoadFailed] = useState(false);
  const [servers, setServers] = useState<Server[]>([]);
  const [dnsDomains, setDnsDomains] = useState<string[]>([]);
  const [dnsDefault, setDnsDefault] = useState<string | null>(null);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [gitlabDefaultRef, setGitlabDefaultRef] = useState<string | null>(null);
  const [showAdvancedNew, setShowAdvancedNew] = useState(false);
  const [showAdvancedExisting, setShowAdvancedExisting] = useState(false);
  const [newError, setNewError] = useState<string | null>(null);
  const [existingError, setExistingError] = useState<string | null>(null);
  const [newGitlabUrl, setNewGitlabUrl] = useState('');
  const [existingGitlabUrl, setExistingGitlabUrl] = useState('');
  const [newTemplateId, setNewTemplateId] = useState('');
  const [newTemplateUrl, setNewTemplateUrl] = useState('');
  const [quickstartType, setQuickstartType] = useState<'none' | 'docker_compose' | 'getting_started'>('none');
  const [quickstartInput, setQuickstartInput] = useState('');
  const [health, setHealth] = useState<SanityChecksResponse | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const appsLoadGenerationRef = useRef(0);
  const healthLoadGenerationRef = useRef(0);

  const clearProjectScopedData = useCallback(() => {
    // A membership can be revoked while this page is backgrounded. Avoid
    // retaining even the safe app projection until foreground revalidation
    // has completed successfully.
    setApps([]);
    setServers([]);
    setDnsDomains([]);
    setDnsDefault(null);
    setTemplates([]);
    setGitlabDefaultRef(null);
    setHealth(null);
    setHealthError(null);
  }, []);

  const loadApps = useCallback(async () => {
    const generation = appsLoadGenerationRef.current + 1;
    appsLoadGenerationRef.current = generation;
    setAppsLoading(true);
    setAppsLoadFailed(false);
    try {
      const data = await apiClient.get<AppItem[]>('/api/apps');
      if (appsLoadGenerationRef.current === generation) {
        setApps(data);
      }
    } catch (err) {
      if (appsLoadGenerationRef.current !== generation) {
        return;
      }
      setApps([]);
      setAppsLoadFailed(true);
      reportError('Failed to load apps', err, setError);
    } finally {
      if (appsLoadGenerationRef.current === generation) {
        setAppsLoading(false);
      }
    }
  }, [apiClient, setError]);

  const loadServers = useCallback(async () => {
    try {
      const data = await apiClient.get<Server[]>('/api/servers');
      setServers(data);
    } catch (err) {
      reportError('Failed to load servers', err, setError);
    }
  }, [apiClient, setError]);

  const loadDomains = useCallback(async () => {
    try {
      const data = await apiClient.get<DnsDomainsResponse>('/api/dns/domains');
      setDnsDomains(data.domains ?? []);
      setDnsDefault(data.default_domain ?? null);
    } catch (err) {
      setDnsDomains([]);
      setDnsDefault(null);
      reportError('Failed to load DNS domains', err, setError);
    }
  }, [apiClient, setError]);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await apiClient.get<TemplateResponse>('/api/apps/templates');
      setTemplates(data.templates ?? []);
    } catch (err) {
      setTemplates([]);
      reportError('Failed to load templates', err, setError);
    }
  }, [apiClient, setError]);

  const loadAppDefaults = useCallback(async () => {
    try {
      const data = await apiClient.get<AppDefaultsResponse>('/api/apps/defaults');
      setGitlabDefaultRef(data.gitlab_default_ref?.trim() || null);
    } catch (err) {
      setGitlabDefaultRef(null);
      reportError('Failed to load app defaults', err, setError);
    }
  }, [apiClient, setError]);

  const loadHealth = useCallback(async () => {
    const generation = healthLoadGenerationRef.current + 1;
    healthLoadGenerationRef.current = generation;
    try {
      const data = await apiClient.get<SanityChecksResponse>('/api/settings/sanity');
      if (healthLoadGenerationRef.current === generation) {
        setHealth(data);
        setHealthError(null);
      }
    } catch (err) {
      if (healthLoadGenerationRef.current === generation) {
        setHealth(null);
        setHealthError(err instanceof Error ? err.message : 'Failed to load health checks');
      }
    }
  }, [apiClient]);

  const loadPageData = useCallback(async () => {
    // A project member without the legacy ViewSite role may only receive the
    // safe /api/apps projection. Do not probe topology, DNS, templates, or
    // creation defaults that their membership does not authorize.
    const tasks = [loadApps()];
    // Platform sanity checks include active gateway probes and belong on the
    // administrator Settings page. Project owners use this aggregate only for
    // their permission-scoped app reports; every app page also checks itself.
    if (!isAdmin && apiClient.mode !== 'tool-browser') {
      tasks.push(loadHealth());
    }
    if (canManageApps) {
      tasks.push(loadDomains(), loadTemplates(), loadAppDefaults());
      if (isAdmin) {
        tasks.push(loadServers());
      }
    }
    await Promise.all(tasks);
  }, [apiClient.mode, canManageApps, isAdmin, loadAppDefaults, loadApps, loadDomains, loadHealth, loadServers, loadTemplates]);

  useEffect(() => {
    // The initial page fetch intentionally populates local state.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadPageData().catch(() => null);
  }, [loadPageData]);

  useEffect(() => {
    const refreshAfterForeground = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      clearProjectScopedData();
      void loadPageData().catch(() => null);
    };
    document.addEventListener('visibilitychange', refreshAfterForeground);
    return () => document.removeEventListener('visibilitychange', refreshAfterForeground);
  }, [clearProjectScopedData, loadPageData]);

  async function createApp(e: React.FormEvent<HTMLFormElement>, mode: 'new' | 'existing') {
    e.preventDefault();
    const formEl = e.currentTarget;
    const form = new FormData(formEl);
    const dnsDomain = String(form.get('dns_domain') ?? '').trim();
    const dnsSubdomain = String(form.get('dns_subdomain') ?? '').trim();
    const dnsUseApex = isAdmin && form.get('dns_use_apex') === 'on';
    const nameValue = String(form.get('name') ?? '').trim();
    const gitlabUrl = String(form.get('gitlab_url') ?? '').trim();
    const templateKey = String(form.get('template') ?? '').trim();
    const templateUrl = String(form.get('template_url') ?? '').trim();
    const createHook = form.get('gitlab_create_hook') === 'on';
    const fallbackSubdomain = dnsSubdomain || slugifyName(nameValue) || '';
    const dnsRecordName =
      dnsDomain && dnsUseApex
        ? dnsDomain
        : dnsDomain && fallbackSubdomain
        ? `${fallbackSubdomain}.${dnsDomain}`
        : dnsSubdomain.includes('.')
          ? dnsSubdomain
          : null;
    if (!nameValue) {
      const message = 'Name required';
      setError(message);
      if (mode === 'existing') {
        setExistingError(message);
      } else {
        setNewError(message);
      }
      return;
    }
    // Template is optional — empty repo will be created if none chosen
    if (mode === 'existing') {
      if (!gitlabUrl) {
        setError('GitLab URL required for existing apps');
        setExistingError('GitLab URL required for existing apps');
        return;
      }
    }
    const payload = {
      name: nameValue,
      gitlab_url: gitlabUrl || null,
      gitlab_project_path: null,
      gitlab_ref: String(form.get('gitlab_ref') ?? '').trim() || null,
      production_server_id: String(form.get('production_server_id') ?? '') || null,
      conversation_server_id: String(form.get('conversation_server_id') ?? '') || null,
      dns_record_name: dnsRecordName,
      template: mode === 'new' ? templateKey || null : null,
      template_path: mode === 'new' && !templateKey ? templateUrl || null : null,
      quickstart_type: quickstartType !== 'none' ? quickstartType : null,
      quickstart_input: quickstartInput.trim() || null
    };
    try {
      const created = await apiClient.post<QuickstartAppResponse>(`/api/apps/${mode}`, payload);
      if (createHook) {
        try {
          await apiClient.post(`/api/apps/${created.app.id}/gitlab/hook`, {});
        } catch (hookErr) {
          reportErrorLocal(
            'Created app, but failed to create GitLab hook',
            hookErr,
            setError,
            mode === 'existing' ? setExistingError : setNewError
          );
        }
      }
      formEl.reset();
      if (mode === 'existing') {
        setExistingError(null);
      } else {
        setNewError(null);
        setNewTemplateId('');
        setNewTemplateUrl('');
        setQuickstartType('none');
        setQuickstartInput('');
      }
      await loadApps();

      // If a quickstart conversation was created, navigate to it
      if (created.conversation_id) {
        navigate(`/conversations/${created.conversation_id}`);
      } else {
        navigate(`/apps/${created.app.id}`);
      }
    } catch (err) {
      if (mode === 'existing') {
        reportErrorLocal('Failed to create app', err, setError, setExistingError);
      } else {
        reportErrorLocal('Failed to create app', err, setError, setNewError);
      }
    }
  }

  const serverOptions = servers.map((server) => (
    <option key={server.id} value={server.id}>
      {server.name} ({server.host})
    </option>
  ));

  const domainOptions = dnsDomains.map((domain) => (
    <option key={domain} value={domain}>
      {domain}
    </option>
  ));

  const templateOptions = templates.map((template) => (
    <option key={template.id} value={template.id}>
      {template.name} ({template.slug})
    </option>
  ));
  const appHealthReports = health?.apps.filter((report) => report.checks.length > 0) ?? [];

  function renderDnsFields() {
    return (
      <>
        <select name="dns_domain" defaultValue={dnsDefault ?? ''} disabled={dnsDomains.length === 0}>
          <option value="">
            {dnsDomains.length === 0 ? 'No DNS domains available' : 'DNS domain'}
          </option>
          {domainOptions}
        </select>
        <input name="dns_subdomain" placeholder="Subdomain (app)" />
        {isAdmin && (
          <label className="checkbox-row">
            <input type="checkbox" name="dns_use_apex" />
            <span>Use apex</span>
          </label>
        )}
      </>
    );
  }

  return (
    <section className="grid">
      {(healthError || appHealthReports.length > 0) && (
        <div className="card full">
          <div className="card-header">
            <div>
              <h2>Health</h2>
              <p className="status">Only applications you administer are shown.</p>
            </div>
            <button onClick={() => void loadHealth()}>Refresh</button>
          </div>
          {healthError && <ErrorBanner message={healthError} onDismiss={() => setHealthError(null)} />}
          {appHealthReports.map((report) => {
            const app = apps.find((candidate) => candidate.id === report.app_id);
            return (
              <div className="stack" key={report.app_id}>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => navigate(`/apps/${report.app_id}`)}
                >
                  {app?.name ?? `Application ${report.app_id.slice(0, 8)}`}
                </button>
                <HealthChecksList checks={report.checks} />
              </div>
            );
          })}
        </div>
      )}
      {canManageApps && <div className="card">
        <h2>Create new app</h2>
        <form onSubmit={(e) => createApp(e, 'new')}>
          {newError && <ErrorBanner message={newError} onDismiss={() => setNewError(null)} />}
          <input name="name" placeholder="Name" required />
          {templates.length > 0 ? (
            <>
              <select
                name="template"
                value={newTemplateId}
                onChange={(e) => {
                  setNewTemplateId(e.target.value);
                  if (e.target.value) {
                    setNewTemplateUrl('');
                  }
                }}
              >
                <option value="">Template</option>
                {templateOptions}
              </select>
              {!newTemplateId && (
                <>
                  <input
                    name="template_url"
                    placeholder="Template URL"
                    value={newTemplateUrl}
                    onChange={(e) => setNewTemplateUrl(e.target.value)}
                  />
                  {newTemplateUrl.trim() && (
                    <p className="status warning">
                      Custom template URLs may not support the full automation experience.
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <input
                name="template_url"
                placeholder="Template URL"
                value={newTemplateUrl}
                onChange={(e) => setNewTemplateUrl(e.target.value)}
              />
              {newTemplateUrl.trim() && (
                <p className="status warning">
                  Custom template URLs may not support the full automation experience.
                </p>
              )}
            </>
          )}
          {isAdmin && (
            <button
              type="button"
              className="link-button"
              onClick={() => setShowAdvancedNew((value) => !value)}
            >
              {showAdvancedNew ? 'Hide advanced' : 'Show advanced'}
            </button>
          )}
          {isAdmin && showAdvancedNew && (
            <>
              <input
                name="gitlab_url"
                placeholder="GitLab URL (optional)"
                value={newGitlabUrl}
                onChange={(e) => setNewGitlabUrl(e.target.value)}
              />
              {renderGitlabPreview(newGitlabUrl)}
              <input
                key={`new-ref-${gitlabDefaultRef ?? 'unset'}`}
                name="gitlab_ref"
                placeholder={`GitLab ref (${gitlabDefaultRef ?? 'default'})`}
                defaultValue={gitlabDefaultRef ?? ''}
              />
              <label className="row">
                <input type="checkbox" name="gitlab_create_hook" />
                Create GitLab hook
              </label>
              <select name="production_server_id" defaultValue="">
                <option value="">Production server</option>
                {serverOptions}
              </select>
              <select name="conversation_server_id" defaultValue="">
                <option value="">Conversation server</option>
                {serverOptions}
              </select>
            </>
          )}
          <fieldset>
            <legend>Quickstart (optional)</legend>
            <p className="status">
              Optionally provide a docker-compose file or getting started documentation.
              We'll create a conversation with an agent to help generate your ops.yaml.
            </p>
            <select
              value={quickstartType}
              onChange={(e) => {
                setQuickstartType(e.target.value as 'none' | 'docker_compose' | 'getting_started');
                if (e.target.value === 'none') {
                  setQuickstartInput('');
                }
              }}
            >
              <option value="none">No quickstart</option>
              <option value="docker_compose">Docker Compose</option>
              <option value="getting_started">Getting Started Docs</option>
            </select>
            {quickstartType !== 'none' && (
              <textarea
                placeholder="Paste your docker-compose.yml or getting started documentation here..."
                value={quickstartInput}
                onChange={(e) => setQuickstartInput(e.target.value)}
                rows={15}
                className="mono"
              />
            )}
          </fieldset>
          {renderDnsFields()}
          <button type="submit">Create</button>
        </form>
      </div>}

      {isAdmin && (
        <div className="card">
          <h2>Add existing app</h2>
          <form onSubmit={(e) => createApp(e, 'existing')}>
            {existingError && (
              <ErrorBanner message={existingError} onDismiss={() => setExistingError(null)} />
            )}
            <input name="name" placeholder="Name" required />
            <input
              name="gitlab_url"
              placeholder="GitLab URL"
              value={existingGitlabUrl}
              onChange={(e) => setExistingGitlabUrl(e.target.value)}
            />
            {renderGitlabPreview(existingGitlabUrl)}
            <button
              type="button"
              className="link-button"
              onClick={() => setShowAdvancedExisting((value) => !value)}
            >
              {showAdvancedExisting ? 'Hide advanced' : 'Show advanced'}
            </button>
            {showAdvancedExisting && (
              <>
                <input
                  key={`existing-ref-${gitlabDefaultRef ?? 'unset'}`}
                  name="gitlab_ref"
                  placeholder={`GitLab ref (${gitlabDefaultRef ?? 'default'})`}
                  defaultValue={gitlabDefaultRef ?? ''}
                />
                <label className="row">
                  <input type="checkbox" name="gitlab_create_hook" />
                  Create GitLab hook
                </label>
                <select name="production_server_id" defaultValue="">
                  <option value="">Production server</option>
                  {serverOptions}
                </select>
                <select name="conversation_server_id" defaultValue="">
                  <option value="">Conversation server</option>
                  {serverOptions}
                </select>
              </>
            )}
            {renderDnsFields()}
            <button type="submit">Add</button>
          </form>
        </div>
      )}

      <AppsPageList
        apps={apps}
        servers={servers}
        loading={appsLoading}
        loadFailed={appsLoadFailed}
        onRefresh={loadApps}
      />
    </section>
  );
}

function slugifyName(value: string): string {
  const trimmed = value.trim().toLowerCase();
  const replaced = trimmed.replace(/[^a-z0-9]+/g, '-');
  return replaced.replace(/^-+/, '').replace(/-+$/, '');
}

function renderGitlabPreview(input: string) {
  const path = deriveGitlabProjectPath(input);
  if (!path) {
    return null;
  }
  return <p className="status">Project path: {path}</p>;
}

function deriveGitlabProjectPath(input: string): string | null {
  if (!input) {
    return null;
  }
  const trimmed = input.trim();
  if (trimmed.startsWith('git@')) {
    const after = trimmed.split(':')[1] ?? '';
    return normalizeGitlabPath(after);
  }
  try {
    const url = new URL(trimmed);
    if (!url.pathname) {
      return null;
    }
    return normalizeGitlabPath(url.pathname);
  } catch (err) {
    return normalizeGitlabPath(trimmed);
  }
}

function normalizeGitlabPath(path: string): string | null {
  const trimmed = path.trim().replace(/^\/+/, '').replace(/\.git$/, '');
  if (!trimmed || !trimmed.includes('/')) {
    return null;
  }
  return trimmed;
}
